import { Injectable, inject, signal, computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { UserProfileApiService } from '../services/user-profile-api.service';

import {
  UserDTO,
  LoginDTO,
  AuthResponseDTO,
  ChangePasswordDTO,
  UpdateProfileDTO,
  UserSessionDTO,
} from '../../../types';
import { TApiErrorResponse } from '../../../http';

// User Profile State
interface UserProfileState {
  user: UserDTO | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | TApiErrorResponse | null;
  tokens: {
    access_token: string | null;
    refresh_token: string | null;
    expires_in: number | null;
  };
  sessionId: string | null;
  sessions: UserSessionDTO[];
  isLoadingSessions: boolean;
  sessionsError: string | null;
  mustResetPassword: boolean;
}

// Initial state
const initialState: UserProfileState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  tokens: {
    access_token: null,
    refresh_token: null,
    expires_in: null,
  },
  sessionId: null,
  sessions: [],
  isLoadingSessions: false,
  sessionsError: null,
  mustResetPassword: false,
};

@Injectable({
  providedIn: 'root',
})
export class UserProfileStore {
  private readonly apiService = inject(UserProfileApiService);

  // State signals
  private readonly state = signal<UserProfileState>(initialState);

  // Selectors
  readonly user = computed(() => this.state().user);
  readonly isAuthenticated = computed(() => this.state().isAuthenticated);
  readonly isLoading = computed(() => this.state().isLoading);
  readonly error = computed(() => this.state().error);
  readonly tokens = computed(() => this.state().tokens);
  readonly sessionId = computed(() => this.state().sessionId);
  readonly sessions = computed(() => this.state().sessions);
  readonly isLoadingSessions = computed(() => this.state().isLoadingSessions);
  readonly sessionsError = computed(() => this.state().sessionsError);

  // Computed properties
  readonly isLoggedIn = computed(() => {
    return !!this.user() && !!this.tokens().access_token;
  });

  readonly userFullName = computed(() => {
    const user = this.user();
    return user ? `${user.first_name} ${user.last_name}` : '';
  });

  readonly isTokenExpired = computed(() => {
    const expiresIn = this.tokens().expires_in;
    if (!expiresIn) return true;
    return Date.now() >= expiresIn * 1000;
  });

  // Private method to update state
  private updateState(partial: Partial<UserProfileState>) {
    this.state.update((current) => ({ ...current, ...partial }));
  }

  // Public methods
  setLoading(loading: boolean) {
    this.updateState({ isLoading: loading });
  }

  setError(error: string | null) {
    this.updateState({ error });
  }

  clearError() {
    this.updateState({ error: null });
  }

  setSessionsLoading(loading: boolean) {
    this.updateState({ isLoadingSessions: loading });
  }

  setSessionsError(error: string | null) {
    this.updateState({ sessionsError: error });
  }

  /**
   * Set auth data
   * @param authResponse - AuthResponseDTO
   */
  setAuthData(authResponse: AuthResponseDTO) {
    const mustReset =
      authResponse.must_reset_password ??
      authResponse.user?.must_reset_password ??
      false;
    const userWithFlag = {
      ...authResponse.user,
      must_reset_password: mustReset,
    };
    this.updateState({
      user: userWithFlag,
      isAuthenticated: true,
      tokens: {
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token,
        expires_in: authResponse.expires_in,
      },
      sessionId: authResponse.session_id || null,
      mustResetPassword: mustReset,
      error: null,
    });

    // Store tokens in localStorage for persistence
    localStorage.setItem('access_token', authResponse.access_token);
    localStorage.setItem('refresh_token', authResponse.refresh_token);
    localStorage.setItem('expires_in', authResponse.expires_in.toString());
    localStorage.setItem('user', JSON.stringify(userWithFlag));

    // Store session ID if available
    if (authResponse.session_id) {
      localStorage.setItem('session_id', authResponse.session_id);
    }
  }

  clearAuthData() {
    this.state.set(initialState);

    // Clear localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('expires_in');
    localStorage.removeItem('user');
    localStorage.removeItem('session_id');
  }

  /**
   * Load auth data from localStorage
   */
  loadFromStorage() {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const expiresIn = localStorage.getItem('expires_in');
    const userJson = localStorage.getItem('user');
    const sessionId = localStorage.getItem('session_id');

    if (accessToken && refreshToken && expiresIn && userJson) {
      try {
        const user: UserDTO = JSON.parse(userJson);
        this.updateState({
          user,
          isAuthenticated: true,
          tokens: {
            access_token: accessToken,
            refresh_token: refreshToken,
            expires_in: parseInt(expiresIn, 10),
          },
          sessionId: sessionId,
          mustResetPassword: !!user?.must_reset_password,
        });
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        // Clear auth data by resetting to initial state
        this.state.set(initialState);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('expires_in');
        localStorage.removeItem('user');
        localStorage.removeItem('session_id');
      }
    }
  }

  /**
   * Check if user is authenticated
   */
  isUserAuthenticated(): boolean {
    return this.isLoggedIn() && !this.isTokenExpired();
  }

  /**
   * Login a user
   * @param loginData - LoginDTO
   * @returns Promise<void>
   */
  async login(loginData: LoginDTO): Promise<void> {
    this.updateState({ isLoading: true, error: null });

    try {
      const response = await firstValueFrom(this.apiService.login(loginData));
      if (response) {
        const mustReset =
          response.data.must_reset_password ??
          response.data.user?.must_reset_password ??
          false;
        const userWithFlag = {
          ...response.data.user,
          must_reset_password: mustReset,
        };
        this.updateState({
          user: userWithFlag,
          isAuthenticated: true,
          tokens: {
            access_token: response.data.access_token,
            refresh_token: response.data.refresh_token,
            expires_in: response.data.expires_in,
          },
          sessionId: response.data.session_id || null,
          mustResetPassword: mustReset,
          error: null,
        });

        // Store tokens in localStorage for persistence
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        localStorage.setItem('expires_in', response.data.expires_in.toString());
        localStorage.setItem('user', JSON.stringify(userWithFlag));

        // Store session ID if available
        if (response.data.session_id) {
          localStorage.setItem('session_id', response.data.session_id);
        }
      }
    } catch (error) {
      this.updateState({
        error: error instanceof Error ? error.message : 'Login failed',
      });
    } finally {
      this.updateState({ isLoading: false });
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    const refreshToken = this.tokens().refresh_token;
    const sessionId = this.sessionId();

    if (refreshToken) {
      try {
        await firstValueFrom(
          this.apiService.logout({
            refresh_token: refreshToken,
            session_id: sessionId || undefined,
          })
        );
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    // Clear auth data regardless of API call result
    this.state.set(initialState);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('expires_in');
    localStorage.removeItem('user');
    localStorage.removeItem('session_id');
  }

  /**
   * Update user profile
   * @param profileData - UpdateProfileDTO
   */
  async updateProfile(profileData: UpdateProfileDTO): Promise<void> {
    this.updateState({ isLoading: true, error: null });

    try {
      await firstValueFrom(this.apiService.updateProfile(profileData));

      // Update local user data
      const currentUser = this.user();
      if (currentUser) {
        const updatedUser = { ...currentUser, ...profileData };
        this.updateState({ user: updatedUser });
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      this.updateState({
        error: error instanceof Error ? error.message : 'Profile update failed',
      });
    } finally {
      this.updateState({ isLoading: false });
    }
  }

  /**
   * Change password
   * @param passwordData - ChangePasswordDTO
   */
  async changePassword(passwordData: ChangePasswordDTO): Promise<void> {
    this.updateState({ isLoading: true, error: null });

    try {
      await firstValueFrom(this.apiService.changePassword(passwordData));
      const currentUser = this.user();
      const updatedUser = currentUser
        ? { ...currentUser, must_reset_password: false }
        : null;

      this.updateState({
        mustResetPassword: false,
        user: updatedUser,
      });

      if (updatedUser) {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      this.updateState({
        error:
          error instanceof Error ? error.message : 'Password change failed',
      });
      throw error;
    } finally {
      this.updateState({ isLoading: false });
    }
  }

  /**
   * Load user sessions
   */
  async loadUserSessions(): Promise<void> {
    this.updateState({ isLoadingSessions: true, sessionsError: null });

    try {
      const response = await firstValueFrom(this.apiService.getUserSessions());
      this.updateState({
        sessions: response.data.map((session) => ({
          ...session,
          is_current_session: session.session_id === this.sessionId(),
        })),
      });
    } catch (error) {
      this.updateState({
        sessionsError:
          error instanceof Error ? error.message : 'Failed to load sessions',
      });
    } finally {
      this.updateState({ isLoadingSessions: false });
    }
  }

  /**
   * Invalidate a session
   * @param sessionId - string
   */
  async invalidateSession(sessionId: string): Promise<void> {
    this.updateState({ isLoadingSessions: true, sessionsError: null });

    try {
      await firstValueFrom(this.apiService.invalidateSession(sessionId));

      // Remove the session from the local state
      const currentSessions = this.sessions();
      const updatedSessions = currentSessions.filter(
        (session) => session.session_id !== sessionId
      );
      this.updateState({ sessions: updatedSessions });
    } catch (error) {
      this.updateState({
        sessionsError:
          error instanceof Error
            ? error.message
            : 'Failed to invalidate session',
      });
      throw error;
    } finally {
      this.updateState({ isLoadingSessions: false });
    }
  }
}

export type UserProfileStoreType = UserProfileStore;
